/**
 * SWUpdateBanner.tsx — "New version available" PWA update prompt
 *
 * Listens for the `kapi:sw-update` custom DOM event dispatched by main.tsx
 * when a new Service Worker is waiting. Shows a dismissible banner with a
 * "Actualizar" button that triggers SW skipWaiting → page reload.
 *
 * Usage (App.tsx or Layout.tsx):
 *   <SWUpdateBanner />
 *
 * MIGRATION_PLAN.md § 3.3.5 — Offline-First POS
 */

import React, { useCallback, useEffect, useState } from 'react';
import { applyUpdate } from '../lib/register-sw';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SWUpdateEvent extends CustomEvent {
  detail: { registration: ServiceWorkerRegistration };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * SWUpdateBanner — renders a non-blocking update prompt when a new SW is ready.
 * Renders nothing until an update is detected.
 */
export function SWUpdateBanner(): React.ReactElement | null {
  const [pendingRegistration, setPendingRegistration] =
    useState<ServiceWorkerRegistration | null>(null);

  // Listen for update events dispatched from main.tsx
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as SWUpdateEvent;
      setPendingRegistration(customEvent.detail.registration);
    };

    window.addEventListener('kapi:sw-update', handler);
    return () => window.removeEventListener('kapi:sw-update', handler);
  }, []);

  const handleUpdate = useCallback(() => {
    if (!pendingRegistration) return;
    applyUpdate(pendingRegistration);
    // Page will reload automatically via controllerchange listener in register-sw.ts
  }, [pendingRegistration]);

  const handleDismiss = useCallback(() => {
    setPendingRegistration(null);
  }, []);

  if (!pendingRegistration) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg bg-slate-800 text-white px-4 py-3 shadow-xl text-sm max-w-md w-full"
    >
      <span className="flex-1">
        🚀 Nueva versión disponible. Actualiza para continuar.
      </span>
      <button
        type="button"
        onClick={handleUpdate}
        className="rounded bg-indigo-500 hover:bg-indigo-400 px-3 py-1 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        aria-label="Actualizar Kapi POS a la nueva versión"
      >
        Actualizar
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        className="rounded hover:bg-white/10 p-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        aria-label="Descartar notificación de actualización"
      >
        ✕
      </button>
    </div>
  );
}
